import { useMutation } from '@apollo/client'
import { Nullable } from '@dai/common'
import { useTypedFormik } from '@dai/components'
import { TypedFormikProps } from '@dai/components/src/formik/types'
import InfoIcon from '@mui/icons-material/Info'
import {
  Alert,
  ButtonProps,
  Checkbox,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { isEqual } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { usePlacesWidget } from 'react-google-autocomplete'
import * as Yup from 'yup'

import {
  CREATE_STORE,
  CreateLocationInput,
  CreateStoreMutation,
  CreateStoreMutationVariables,
  StoreFragment,
  UPDATE_STORE,
  UpdateStoreMutation,
  UpdateStoreMutationVariables,
} from '@/graphql'
import useT from '@/i18n/useT'
import { Address, LocationHelpers } from '@/utils/location.helpers'

const { VITE_APP__GOOGLE_MAPS_API_KEY } = import.meta.env

export type FormValues = {
  name: string
  address: string
  isActive: boolean
}

export type OnSubmitProps = {
  mode: 'add' | 'edit'
  storeId: Nullable<string>
  values: FormValues
  address: Address
  setStatus: (status: { success: boolean; message?: string }) => void
}

export type StoreFormProps = (
  | { mode: 'add'; store?: StoreFragment | null }
  | { mode: 'edit'; store: StoreFragment }
) & {
  onSubmit: (props: OnSubmitProps) => Promise<void>
  onCancel?: () => void
  otherButtons?: ButtonProps[]
  enableReinitialize?: boolean
}

export const useValidationSchema = () => {
  const { t } = useT()

  return Yup.object().shape({
    name: Yup.string()
      .max(100, t('addEditStore.shared.name.max'))
      .required(t('addEditStore.shared.name.required')),
    address: Yup.string().required(t('addEditStore.shared.address.required')),
  })
}

export const useCreateEditStore = ({
  onSuccess,
}: {
  onSuccess?: (store: Nullable<StoreFragment>) => void
}) => {
  const { t } = useT()

  const [createStore, { loading: creatingStore }] = useMutation<
    CreateStoreMutation,
    CreateStoreMutationVariables
  >(CREATE_STORE)

  const [updateStore, { loading: updatingStore }] = useMutation<
    UpdateStoreMutation,
    UpdateStoreMutationVariables
  >(UPDATE_STORE)

  const onSubmit = async ({
    mode,
    storeId,
    values,
    address,
    setStatus,
  }: OnSubmitProps) => {
    try {
      const locationData: CreateLocationInput = {
        name: values.name,
        address1: address.address1,
        city: address.city,
        county: address.county || '',
        state: address.state,
        postalCode: address.postalCode,
      }

      if (mode === 'add') {
        const result = await createStore({
          variables: {
            data: {
              location: locationData,
              inactiveAt: values.isActive ? null : new Date(),
            },
          },
        })
        onSuccess?.(result.data?.createStore)
      } else if (storeId) {
        const result = await updateStore({
          variables: {
            data: {
              id: storeId,
              location: locationData,
              inactiveAt: values.isActive ? null : new Date(),
            },
          },
        })
        onSuccess?.(result.data?.updateStore)
      } else {
        setStatus({
          success: false,
          message: t('addEditStore.shared.storeNotFound'),
        })
      }
      setStatus({ success: true })
    } catch (error: any) {
      const message = error.message || t('common.somethingWentWrong')
      setStatus({ success: false, message })
    }
  }

  return {
    onSubmit,
    saving: creatingStore || updatingStore,
  }
}

export const useStoreForm = (props: StoreFormProps) => {
  const { store, mode, onSubmit, enableReinitialize } = props
  const { t } = useT()

  const [validatedAddress, setValidatedAddress] = useState<string | null>(
    store?.location ? LocationHelpers.toString(store.location) : null,
  )
  const [address, setAddress] = useState<Address | null | undefined>(
    store?.location,
  )

  const isInitialized = useRef(false)

  const initialValues: FormValues = {
    name: store?.location?.name || '',
    address: store?.location ? LocationHelpers.toString(store.location) : '',
    isActive: !store?.inactiveAt,
  }
  const validationSchema = useValidationSchema()

  const formik = useTypedFormik({
    initialValues,
    validationSchema,
    enableReinitialize,
    onSubmit: async (values, { setStatus }) => {
      try {
        if (
          values.address !== validatedAddress ||
          !address?.address1 ||
          !address.city ||
          !address.county ||
          !address.state ||
          !address.postalCode
        ) {
          setStatus({
            success: false,
            message: t('addEditStore.shared.invalidAddress'),
          })
          return
        }

        await onSubmit({
          mode,
          storeId: store?.id,
          values,
          address,
          setStatus,
        })
        setStatus({ success: true })
      } catch (error: any) {
        const message = error.message || t('common.somethingWentWrong')
        setStatus({ success: false, message })
      }
    },
  })

  useEffect(() => {
    if (mode === 'edit' && store?.location && !isInitialized.current) {
      const addressString = LocationHelpers.toString(store.location)
      setValidatedAddress(addressString)
      formik.setFieldValue('address', addressString)
      isInitialized.current = true
    }
  }, [store, mode, formik])

  const needsToEdit = mode === 'edit' && isEqual(formik.values, initialValues)

  const onPlaceSelected = useCallback(
    ({
      address,
      formattedAddress,
    }: {
      address: Address
      formattedAddress: string
    }) => {
      setAddress(address)
      setValidatedAddress(formattedAddress)
      formik.setFieldValue('address', formattedAddress)
    },
    [formik, setAddress, setValidatedAddress],
  )

  return {
    formik,
    onPlaceSelected,
    needsToEdit,
  }
}

export const StoreFormContent: React.FC<{
  formik: TypedFormikProps<FormValues>
  needsToEdit: boolean
  mode: 'add' | 'edit'
  showIsActive?: boolean
  onPlaceSelected: ({
    address,
    formattedAddress,
  }: {
    address: Address
    formattedAddress: string
  }) => void
}> = ({ formik, onPlaceSelected, showIsActive = true }) => {
  const theme = useTheme()

  useEffect(() => {
    const darkStyleId = 'pac-dark-mode-fix'
    const existingDarkStyle = document.getElementById(darkStyleId)

    if (!existingDarkStyle) {
      const darkStyle = document.createElement('style')
      darkStyle.id = darkStyleId
      darkStyle.textContent = `
        /* Dark mode support for Google Places dropdown */
        @media (prefers-color-scheme: dark) {
          .pac-container {
            background-color: ${theme.palette.background.paper} !important;
            border: 1px solid ${theme.palette.divider} !important;
          }
          
          .pac-item {
            background-color: ${theme.palette.background.paper} !important;
            color: ${theme.palette.text.primary} !important;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
            border-top: none !important;
            cursor: pointer !important;
          }
          
          .pac-item:hover, .pac-item-selected {
            background-color: ${theme.palette.action.hover} !important;
          }
          
          .pac-item-query {
            color: ${theme.palette.text.primary} !important;
          }
          
          .pac-matched {
            color: ${theme.palette.primary.main} !important;
          }
        }
      `
      document.head.appendChild(darkStyle)
    }

    return () => {
      const darkStyle = document.getElementById(darkStyleId)
      if (darkStyle) {
        darkStyle.remove()
      }
    }
  }, [theme])

  const { ref: inputRef } = usePlacesWidget<HTMLInputElement>({
    apiKey: VITE_APP__GOOGLE_MAPS_API_KEY,
    onPlaceSelected: placeResult => {
      onPlaceSelected({
        address:
          LocationHelpers.extractAddressFromGooglePlaceResult(placeResult),
        formattedAddress: placeResult.formatted_address,
      })
    },
    options: {
      types: ['address'],
      componentRestrictions: { country: 'us' },
    },
  })
  const { t } = useT()

  return (
    <Stack spacing={4}>
      {formik.status?.message && (
        <Alert severity={formik.status.success ? 'success' : 'warning'}>
          {formik.status.message}
        </Alert>
      )}
      <TextField
        type="text"
        name="name"
        label={t('addEditStore.shared.name')}
        value={formik.values.name}
        error={Boolean(formik.touched.name && formik.errors.name)}
        fullWidth
        helperText={formik.touched.name && formik.errors.name}
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
      />
      <TextField
        inputRef={inputRef}
        type="text"
        name="address"
        label={t('addEditStore.shared.address')}
        value={formik.values.address}
        error={Boolean(formik.touched.address && formik.errors.address)}
        fullWidth
        helperText={formik.touched.address && formik.errors.address}
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
      />

      {showIsActive && (
        <FormControlLabel
          control={
            <Checkbox
              name="isActive"
              checked={formik.values.isActive}
              onChange={formik.handleChange}
            />
          }
          label={
            <>
              <Grid container direction="row">
                <Typography sx={{ mt: 1.5 }}>
                  {t('addEditStore.shared.active')}
                </Typography>
                <Tooltip title={t('addEditStore.shared.activeTooltip')}>
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
            </>
          }
          sx={{ alignItems: 'center' }}
        />
      )}
    </Stack>
  )
}
